// LA INSTALACIÓN EN EL TELÉFONO: el evento, iOS, «ya instalada» y el
// almacenamiento protegido · DIRECTRIZ-PWA F1 (11 oct 2026, DISENO-F1 §8).
//
// CAPTURA TEMPRANA. Chrome dispara `beforeinstallprompt` poco después de
// cargar; si el que escucha es un componente que todavía no montó, el
// evento se pierde y el botón nunca aparece. Por eso se escucha aquí, al
// importar desde main.jsx, y la pantalla lo lee después.
let eventoInstalar = null;
let instaladaAhora = false;
const oyentes = new Set();
const avisar = () => oyentes.forEach((f) => f());

export const CLAVE_INSTALADA = 'tego.pwa.instalada';
export const CLAVE_PERSISTE = 'tego.pwa.persistente';

function alPedirInstalar(e) {
  // Sin esto Chrome enseña su propio banner y el botón de TEGO no sale.
  e.preventDefault();
  eventoInstalar = e;
  avisar();
}
function alInstalarse() {
  instaladaAhora = true;
  eventoInstalar = null;
  try { localStorage.setItem(CLAVE_INSTALADA, String(Date.now())); } catch { /* sin almacenamiento */ }
  avisar();
}
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('beforeinstallprompt', alPedirInstalar);
  window.addEventListener('appinstalled', alInstalarse);
}

export const eventoDeInstalacion = () => eventoInstalar;
export const suscribir = (f) => { oyentes.add(f); return () => oyentes.delete(f); };

const consulta = (q) => (typeof matchMedia === 'function' ? matchMedia(q) : null);

// YA INSTALADA: la ventana corre a pantalla completa (todas las plataformas)
// o iOS lo dice con su propiedad.
export function estaInstalada() {
  const mm = consulta('(display-mode: standalone)');
  return !!mm?.matches || navigator.standalone === true || instaladaAhora;
}
// iPhone/iPad en Safari (R-PWA-3): la propiedad existe SOLO en WebKit de iOS.
// Nunca se lee el user-agent. Respaldo para el iPad que se presenta como Mac.
export function esIOS() {
  if (typeof navigator.standalone === 'boolean') return true;
  return (navigator.maxTouchPoints || 0) > 1 && /Mac/.test(navigator.platform || '');
}
// Chromium sin leer la cadena del UA: `userAgentData` lo expone como dato.
export const esChromium = () => !!navigator.userAgentData?.brands?.some((b) => /Chrom/i.test(b.brand));
export const esTactil = () => !!consulta('(pointer: coarse)')?.matches;

/** Lanza la pregunta del navegador. Devuelve 'accepted' | 'dismissed' | 'sin-evento'. */
export async function instalar() {
  const e = eventoInstalar;
  if (!e) return 'sin-evento';
  e.prompt();
  const { outcome } = await e.userChoice;
  if (outcome === 'accepted') eventoInstalar = null;
  avisar();
  return outcome;
}

// ALMACENAMIENTO PERSISTENTE (R-PWA-9): se pide UNA vez y se recuerda la
// respuesta. `true` protegido, `false` no, `null` el navegador no lo ofrece.
//
// Auditoría F1 (P2-1 y P2-1b, 11 oct 2026): con «no» recordado NO se vuelve
// a preguntar —en Firefox `persist()` abre un diálogo, y el aviso lo
// disparaba en cada carga—; la segunda oportunidad que da R-PWA-9 («otra
// vez cuando una cola guarde su primer elemento») la pide F2 de forma
// explícita con `volverAPedirPersistencia()`. Y si el aviso y la pantalla
// la piden a la vez, comparten UNA sola petición en vuelo: nunca dos
// `persist()` en paralelo.
let peticionEnVuelo = null;

async function preguntarAlNavegador() {
  const ok = (await navigator.storage.persisted?.()) || (await navigator.storage.persist());
  localStorage.setItem(CLAVE_PERSISTE, ok ? 'si' : 'no');
  return !!ok;
}

export async function pedirPersistencia() {
  try {
    const previo = localStorage.getItem(CLAVE_PERSISTE);
    if (previo === 'si') return true;
    if (previo === 'no') return false;
    if (!navigator.storage?.persist) return null;
    if (!peticionEnVuelo) {
      peticionEnVuelo = preguntarAlNavegador().finally(() => { peticionEnVuelo = null; });
    }
    return await peticionEnVuelo;
  } catch {
    return null;
  }
}

/**
 * La segunda oportunidad de R-PWA-9: la llama F2 cuando una cola guarda su
 * primer elemento y la primera respuesta fue «no». Olvida el «no» y vuelve a
 * preguntar una vez. Con «sí» recordado no hace nada.
 */
export async function volverAPedirPersistencia() {
  try {
    if (localStorage.getItem(CLAVE_PERSISTE) === 'no') localStorage.removeItem(CLAVE_PERSISTE);
  } catch { /* sin almacenamiento: se pide igual */ }
  return pedirPersistencia();
}

/** Lo que se recordó, sin volver a pedir. */
export function persistenciaRecordada() {
  try {
    const v = localStorage.getItem(CLAVE_PERSISTE);
    return v === 'si' ? true : v === 'no' ? false : null;
  } catch { return null; }
}

/* Solo para pruebas. */
export function _reiniciarInstalacion() {
  eventoInstalar = null;
  instaladaAhora = false;
  peticionEnVuelo = null;
  avisar();
}
